import { Component, ViewChild, ElementRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment.uat';

// --- Interface Definitions ---
// Represents a message displayed in the UI
interface DisplayMessage {
  role: 'user' | 'assistant';
  content: string;
  sources?: SourceDocument[];
}

// Represents a source document for an assistant's answer
interface SourceDocument {
  page_content: string;
  metadata: any;
}

// --- API Request/Response Models ---
interface QueryRequest {
  question: string;
  chat_history: [string, string][];
}

interface QueryResponse {
  answer: string;
  source_documents: SourceDocument[];
}

@Component({
  selector: 'app-ai-chat',
  standalone: false,
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.css']
})
export class AiChatComponent {
  @ViewChild('messageArea') private messageArea!: ElementRef;

  public displayMessages: DisplayMessage[] = [];
  public userInput: string = '';
  public isLoading: boolean = false;
  protected portalBaseUrl = "https://portalserver-qa.acimobills.com/portal/api" // environment.apiServer.portalUrl;

  private chatHistory: [string, string][] = [];
  private apiUrl = `${this.portalBaseUrl}/aiagent`; // The backend endpoint

  constructor(private http: HttpClient) {}

  private isUserNearBottom(): boolean {
    const threshold = 50;
    const el = this.messageArea.nativeElement;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    return atBottom;
  }

  private scrollToBottom(): void {
    try {
      this.messageArea.nativeElement.scrollTop = this.messageArea.nativeElement.scrollHeight;
    } catch(err) { }
  }

  sendMessage(): void {
    const question = this.userInput.trim();
    if (!question || this.isLoading) {
      return;
    }

    const wasAtBottom = this.isUserNearBottom();

    // Add user message to the UI
    this.displayMessages.push({ role: 'user', content: question });
    this.userInput = '';
    this.isLoading = true;

    if (wasAtBottom) {
      setTimeout(() => this.scrollToBottom(), 0);
    }

    // Prepare the request payload
    const payload: QueryRequest = {
      question: question,
      chat_history: this.chatHistory
    };

    // Make the API call
    this.http.post<QueryResponse>(this.apiUrl, payload)
      .pipe(finalize(() => {
        this.isLoading = false;
        if (wasAtBottom) {
          setTimeout(() => this.scrollToBottom(), 0);
        }
      }))
      .subscribe({
        next: (response) => {
          const wasAtBottomOnReceive = this.isUserNearBottom();
          // Add assistant response to the UI
          this.displayMessages.push({
            role: 'assistant',
            content: response.answer,
            sources: response.source_documents
          });
          // Update the chat history for the next request
          this.chatHistory.push([question, response.answer]);
          if (wasAtBottomOnReceive) {
            setTimeout(() => this.scrollToBottom(), 0);
          }
        },
        error: (err) => {
          // Handle errors gracefully in the UI
          this.displayMessages.push({
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.'
          });
          console.error(err);
        }
      });
  }
}
